import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useImageAttachmentMutation } from '../../../../queries/Attachments/useImageAttachmentMutation';
import { invalidateAccountLoad, useAccountLoad, useAdminAccountQuery, useAttachmentQuery } from '../../../../queries';
import { useSetGoogleBusinessBrandingSettings } from '../../../../queries/GoogleBusiness/useSetGoogleBusinessBrandingSettings';
import { Button } from '../../../components-v2/Button';
import { Checkbox } from '../../../components-v2/Checkbox';
import { ColorPicker } from '../../../components-v2/ColorPicker';
import { UnifiedImageDialog } from '../../../components-v2/UnifiedImageDialog';
import { baseStyleSheet } from '../../../styles/styles';
import { BackgroundImageCropModal } from '../../TestimonialImageEditor/BackgroundImageCropModal';
import { DEFAULT_COLORS } from '../../TestimonialImageEditor/constants';
import { useGetImageUrl } from '../../TestimonialImageEditor/hooks/useGetImageUrl';
import {
	addColorOverlayToImage,
	TESTIMONIAL_OVERLAY_COLOR,
	TESTIMONIAL_OVERLAY_OPACITY,
} from '../../TestimonialImageEditor/utils';
import { styleSheet } from '../styles';

const IMAGES_FOR_SELECTION = [
	{ imagePath: '/static/images/googleReview/bitmap-1.png', slug: 'testimonial-1' },
	{ imagePath: '/static/images/googleReview/bitmap-2.png', slug: 'testimonial-2' },
	{ imagePath: '/static/images/googleReview/bitmap-3.png', slug: 'testimonial-3' },
];

type GoogleReviewBrandingFormValues = {
	backgroundColor: string;
	foregroundColor: string;
	includeGoogleLogo: boolean;
	includeStars: boolean;
	image?: Api.IFileAttachment;
};

const getDefaultFormValues = (branding?: Api.IGoogleBusinessBrandingSettings): GoogleReviewBrandingFormValues => ({
	backgroundColor: branding?.backgroundColor ?? DEFAULT_COLORS.background,
	foregroundColor: branding?.foregroundColor ?? DEFAULT_COLORS.text,
	includeGoogleLogo: branding?.includeGoogleLogo ?? true,
	includeStars: branding?.includeStars ?? true,
	image: branding?.image,
});

interface IGoogleReviewBrandingSectionProps {
	/** Optional impersonation context. If not provided, uses the current user session's context. */
	impersonationContext?: Api.IImpersonationContext;
}

export const GoogleReviewBrandingSection: React.FC<IGoogleReviewBrandingSectionProps> = ({
	impersonationContext: propImpersonationContext,
}) => {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const impersonationContext = propImpersonationContext ?? userSession.impersonationContext;
	const account = impersonationContext?.account ?? userSession.account;

	const { data: accountDataUser } = useAccountLoad({
		accountId: account?.id ?? '',
		enabled: !impersonationContext,
	});

	const { data: accountDataAdmin } = useAdminAccountQuery({
		accountId: account?.id ?? '',
		enabled: Boolean(impersonationContext),
	});

	const accountData = impersonationContext ? accountDataAdmin : accountDataUser;

	const branding = accountData?.preferences?.googleReviewPostBranding;
	const defaultValues = React.useMemo(() => getDefaultFormValues(branding), [branding]);

	const { handleSubmit, watch, setValue, getValues, reset, formState } = useForm<GoogleReviewBrandingFormValues>({
		defaultValues,
	});

	const { isDirty } = formState;
	const backgroundColor = watch('backgroundColor');
	const foregroundColor = watch('foregroundColor');
	const includeGoogleLogo = watch('includeGoogleLogo');
	const includeStars = watch('includeStars');
	const image = watch('image');

	const imageId = image?.id ?? branding?.image?.id;
	const { data: imageData } = useAttachmentQuery({
		id: imageId ?? '',
		enabled: !!imageId,
		impersonationContext: impersonationContext
			? {
					account: { id: impersonationContext.account?.id },
					user: impersonationContext?.user ? { id: impersonationContext.user?.id } : undefined,
				}
			: undefined,
	});

	const [backgroundImageModalOpen, setBackgroundImageModalOpen] = React.useState(false);
	const [cropModalOpen, setCropModalOpen] = React.useState(false);
	const [pendingCropImageUrl, setPendingCropImageUrl] = React.useState<string | null>(null);
	const originalImageUrlRef = React.useRef<string | null>(null);

	const { getImageUrl } = useGetImageUrl(impersonationContext);

	const { mutateAsync: uploadCroppedImage } = useImageAttachmentMutation({
		onError: () => {
			toast.error('Failed to upload cropped image. Please try again.');
		},
	});

	const openCropModal = (dataUrl: string) => {
		setPendingCropImageUrl(dataUrl);
		setCropModalOpen(true);
	};

	const resolveAndOpenCrop = async (attachment: Api.IFileAttachment) => {
		try {
			const dataUrl = await getImageUrl(attachment);
			originalImageUrlRef.current = dataUrl;
			openCropModal(dataUrl);
		} catch {
			toast.error('Failed to load image. Please try again.');
		}
	};

	const handleImageSelectedFromDialog = (img: Api.IFileAttachment) => {
		setBackgroundImageModalOpen(false);
		if (img.url || img.id) {
			resolveAndOpenCrop(img);
		} else {
			setValue('image', img, { shouldDirty: true });
		}
	};

	const handleSelectImagesFromDialog = (images: { slug: string; fileAttachment: Api.IFileAttachment }[]) => {
		setBackgroundImageModalOpen(false);
		const selected = images[0];
		if (selected?.fileAttachment?.url || selected?.fileAttachment?.id) {
			resolveAndOpenCrop(selected.fileAttachment);
		} else if (selected) {
			setValue('image', selected.fileAttachment, { shouldDirty: true });
		}
	};

	const handleCropComplete = async (croppedFile: File) => {
		const toastId = toast.loading('Uploading cropped image...');
		try {
			const attachments = await uploadCroppedImage({
				files: [croppedFile],
				resizeForSocialMedia: true,
				impersonationContext,
			});
			setValue('image', attachments[0], { shouldDirty: true });
			toast.success('Background image applied!', { id: toastId });
		} catch {
			toast.error('Failed to upload cropped image.', { id: toastId });
		}
	};

	const handleEditCrop = () => {
		if (originalImageUrlRef.current) {
			openCropModal(originalImageUrlRef.current);
		} else if (image) {
			resolveAndOpenCrop(image);
		} else {
			setBackgroundImageModalOpen(true);
		}
	};

	React.useEffect(() => {
		const current = accountData?.preferences?.googleReviewPostBranding;
		reset(getDefaultFormValues(current));
	}, [accountData?.preferences?.googleReviewPostBranding, reset]);

	const brandingMutation = useSetGoogleBusinessBrandingSettings({
		impersonationContext,
		onError: err => {
			errorMessages.pushApiError(err);
		},
		onSuccess: () => {
			reset(getValues());
			invalidateAccountLoad();
			toast.success('Google Review branding settings saved successfully');
		},
	});

	const onValid = (data: GoogleReviewBrandingFormValues) => {
		brandingMutation.mutate({
			backgroundColor: data.backgroundColor,
			foregroundColor: data.foregroundColor,
			includeGoogleLogo: data.includeGoogleLogo,
			includeStars: data.includeStars,
			image: data.image,
		} as Api.IGoogleBusinessBrandingSettings);
	};

	const handleCancel = () => {
		const current = accountData?.preferences?.googleReviewPostBranding;
		reset(getDefaultFormValues(current));
	};

	return (
		<div className='settings-admin-company-container'>
			<div className={css(styleSheet.brandingSubsection)}>
				<div className={css(styleSheet.brandingSubsectionTitle)}>Colors</div>
				<div className={css(styleSheet.brandingColorsRow)}>
					<div className={css(styleSheet.brandingColorItem)}>
						<span className={css(styleSheet.brandingColorLabel)}>Background Color</span>
						<ColorPicker
							value={backgroundColor}
							onChange={c => setValue('backgroundColor', c, { shouldDirty: true })}
						/>
					</div>
					<div className={css(styleSheet.brandingColorItem)}>
						<span className={css(styleSheet.brandingColorLabel)}>Text Color</span>
						<ColorPicker
							value={foregroundColor}
							onChange={c => setValue('foregroundColor', c, { shouldDirty: true })}
						/>
					</div>
				</div>
			</div>

			<div className={css(styleSheet.brandingSubsection)}>
				<div className={css(styleSheet.brandingSubsectionTitle)}>Default Background Image</div>
				<div className={css(styleSheet.brandingImageFieldGroup, styleSheet.brandingAddImageButton)}>
					{!image ? (
						<Button variant='secondary' size='sm' onClick={() => setBackgroundImageModalOpen(true)}>
							+ Add Background Image
						</Button>
					) : (
						<div className={css(styleSheet.brandingImageViewMode)}>
							<div className={css(styleSheet.brandingImageViewContent)}>
								{(image?.url ?? imageData?.url) && (
									<img
										src={image?.url ?? imageData?.url}
										alt='Default background'
										className={css(styleSheet.imagePreviewImg)}
										style={{ maxWidth: 120, maxHeight: 80, objectFit: 'cover' }}
									/>
								)}
							</div>
							<div className={css(styleSheet.buttonContainer)}>
								<Button variant='secondary' size='sm' onClick={handleEditCrop}>
									Crop
								</Button>
								<Button variant='secondary' size='sm' onClick={() => setBackgroundImageModalOpen(true)}>
									Change
								</Button>
								<Button
									variant='destructive-secondary'
									size='sm'
									onClick={() => {
										setValue('image', undefined, { shouldDirty: true });
										originalImageUrlRef.current = null;
									}}
								>
									Remove
								</Button>
							</div>
						</div>
					)}
				</div>
			</div>

			<div className={css(styleSheet.brandingSubsection)}>
				<div className={css(styleSheet.brandingSubsectionTitle)}>Default Toggles</div>
				<div className={css(styleSheet.brandingCustomColorsList)}>
					<label
						className={css(
							baseStyleSheet.flex,
							baseStyleSheet.itemsCenter,
							baseStyleSheet.gap2,
							styleSheet.checkboxLabel
						)}
					>
						<Checkbox
							checked={includeGoogleLogo}
							onCheckedChange={checked => setValue('includeGoogleLogo', checked === true, { shouldDirty: true })}
						/>
						<span>Include Google Logo</span>
					</label>
					<label
						className={css(
							baseStyleSheet.flex,
							baseStyleSheet.itemsCenter,
							baseStyleSheet.gap2,
							styleSheet.checkboxLabel
						)}
					>
						<Checkbox
							checked={includeStars}
							onCheckedChange={checked => setValue('includeStars', checked === true, { shouldDirty: true })}
						/>
						<span>Include Stars</span>
					</label>
				</div>
			</div>

			<div className={css(styleSheet.brandingButtonRow, baseStyleSheet.mt3)}>
				<div className={css(styleSheet.buttonContainer)}>
					<Button
						variant='primary'
						size='sm'
						onClick={handleSubmit(onValid)}
						disabled={!isDirty || brandingMutation.isPending}
					>
						{brandingMutation.isPending ? 'Saving...' : 'Save'}
					</Button>
					<Button variant='secondary' size='sm' onClick={handleCancel} disabled={!isDirty}>
						Cancel
					</Button>
				</div>
			</div>

			<UnifiedImageDialog
				showFreeImagesTab={true}
				showUploadTab={true}
				impersonationContext={impersonationContext}
				resizeForSocialMedia
				initialImage={image}
				initialTab={image ? 'ai-edit' : 'select-image'}
				open={backgroundImageModalOpen}
				onOpenChange={setBackgroundImageModalOpen}
				onImageSelected={handleImageSelectedFromDialog}
				showSelectImageTab
				onSelectImages={handleSelectImagesFromDialog}
				allowMultipleSelect={false}
				imagesForSelection={IMAGES_FOR_SELECTION}
				selectImageTitle='Select a Background Image'
				selectImageSubtitle='Note: Images will have an overlayed color to help with readability. You can change this by editing with AI or applying your brand to the selected image.'
				onPreProcessSelectImage={async file =>
					addColorOverlayToImage(file, TESTIMONIAL_OVERLAY_COLOR, TESTIMONIAL_OVERLAY_OPACITY)
				}
			/>

			{pendingCropImageUrl && (
				<BackgroundImageCropModal
					open={cropModalOpen}
					imageUrl={pendingCropImageUrl}
					onOpenChange={open => {
						setCropModalOpen(open);
						if (!open) {
							setPendingCropImageUrl(null);
						}
					}}
					onCropComplete={handleCropComplete}
				/>
			)}
		</div>
	);
};
